import React from "react";

// Top-level error boundary. Any thrown/uncaught render or lifecycle error in
// the tree renders the fallback below instead of a white screen.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    // Best-effort reload; if reload itself throws (very rare), just clear state.
    try {
      window.location.reload();
    } catch {
      this.setState({ error: null });
    }
  };

  handleGoHome = () => {
    try {
      window.location.assign("/");
    } catch {
      this.setState({ error: null });
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          background: "#f9f8f6",
          color: "#1a1c19",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px",
          fontFamily:
            "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        }}
      >
        <div style={{ maxWidth: 520, textAlign: "left" }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#c85a40",
              marginBottom: 12,
            }}
          >
            Something broke
          </div>
          <h1
            style={{
              fontFamily: "'Fraunces', 'IBM Plex Serif', serif",
              fontSize: 40,
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            The page hit an unexpected error.
          </h1>
          <p style={{ marginTop: 16, fontSize: 14, color: "#5c605a", lineHeight: 1.6 }}>
            Sorry about that — the rest of the app is fine. Reload to try again
            or head back to the home page.
          </p>
          <div style={{ display: "flex", gap: 12, marginTop: 28, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                background: "#1a1c19",
                color: "#f9f8f6",
                padding: "12px 20px",
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                border: 0,
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              data-testid="error-boundary-home"
              style={{
                background: "transparent",
                color: "#1a1c19",
                padding: "12px 20px",
                fontSize: 12,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                border: "1px solid #1a1c19",
                cursor: "pointer",
              }}
            >
              Home
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && (
            <pre
              style={{
                marginTop: 28,
                padding: 16,
                background: "#f2efea",
                border: "1px solid #e5e1d8",
                fontSize: 11,
                color: "#5c605a",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 240,
                overflow: "auto",
              }}
            >
              {String(this.state.error?.stack || this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
